import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import {
  CheckCircle,
  CircleNotch,
  Lightning,
  MagnifyingGlass,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { defaultSkill } from "@reactive-resume/schema";
import { Button, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useState } from "react";

import { useFeatureGate } from "@/client/hooks/use-feature-gate";
import { useTakingLonger } from "@/client/hooks/use-taking-longer";
import { useToast } from "@/client/hooks/use-toast";
import { askAssistantToApply } from "@/client/libs/ai-assist";
import { processAi, reportAiOutcome } from "@/client/services/ai";
import { useResumeStore } from "@/client/stores/resume";

type MatchResult = {
  matchPercentage: number;
  matchedKeywords: string[];
  missingKeywords: string[];
  suggestions: string[];
  summary: string;
};

const KeywordBadge = ({
  keyword,
  matched,
  onAdd,
}: {
  keyword: string;
  matched: boolean;
  onAdd?: (keyword: string) => void;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
      matched
        ? "bg-green-500/10 text-green-600 ring-1 ring-inset ring-green-500/20"
        : "bg-red-500/10 text-red-600 ring-1 ring-inset ring-red-500/20",
      !matched && onAdd && "cursor-pointer hover:bg-red-500/20",
    )}
    role={!matched && onAdd ? "button" : undefined}
    tabIndex={!matched && onAdd ? 0 : undefined}
    title={!matched && onAdd ? t`Add to Skills if you actually have this` : undefined}
    onClick={!matched && onAdd ? () => onAdd(keyword) : undefined}
    onKeyDown={
      !matched && onAdd
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onAdd(keyword);
            }
          }
        : undefined
    }
  >
    {matched ? <CheckCircle size={10} weight="fill" /> : <XCircle size={10} weight="fill" />}
    {keyword}
  </span>
);

const MatchGauge = ({ percentage }: { percentage: number }) => {
  const color =
    percentage >= 70 ? "text-green-500" : percentage >= 50 ? "text-yellow-500" : "text-red-500";
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative mx-auto h-24 w-24">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 80 80">
        <circle
          cx="40"
          cy="40"
          r="36"
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          className="text-border/30"
        />
        <motion.circle
          cx="40"
          cy="40"
          r="36"
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={color}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className={cn("text-xl font-bold tabular-nums", color)}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          {percentage}%
        </motion.span>
        <span className="text-[9px] text-foreground/40">{t`match`}</span>
      </div>
    </div>
  );
};

export const JobMatchingSection = () => {
  const { toast } = useToast();
  const { gate } = useFeatureGate();
  const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const takingLonger = useTakingLonger(loading);
  const [result, setResult] = useState<MatchResult | null>(null);
  const setValue = useResumeStore((state) => state.setValue);
  const skillItems = useResumeStore((state) => state.resume.data.sections.skills.items);

  const addKeywordToSkills = useCallback(
    (keyword: string) => {
      const trimmed = keyword.trim();
      if (!trimmed || trimmed.length > 40) return;

      const already = skillItems.some(
        (item) =>
          item.name.toLowerCase() === trimmed.toLowerCase() ||
          item.keywords.some((entry) => entry.toLowerCase() === trimmed.toLowerCase()),
      );
      if (already) {
        toast({
          variant: "info",
          title: t`Already on your resume`,
          description: trimmed,
        });
        return;
      }

      setValue("sections.skills.items", [
        ...skillItems,
        { ...defaultSkill, id: createId(), name: trimmed, keywords: [trimmed], level: 0 },
      ]);
      toast({
        variant: "success",
        title: t`Added to Skills`,
        description: t`Only keep this if it is work you have actually done.`,
      });
    },
    [setValue, skillItems, toast],
  );

  const handleMatch = useCallback(async () => {
    if (!gate("job-match")) return;
    if (!jobDescription.trim()) {
      toast({
        variant: "info",
        title: t`Paste a job description`,
        description: t`Paste the full job description to see how your resume matches.`,
      });
      return;
    }

    const { resume } = useResumeStore.getState();

    setLoading(true);
    try {
      const response = await processAi({
        action: "analyze-job-match",
        jobDescription,
        context: { resumeData: resume.data },
      });

      if (response.ok === false) {
        toast({
          variant: "error",
          title: t`Please try again`,
          description: response.result,
        });
        return;
      }

      const matchedKeywords = (response.metadata?.matchedKeywords ?? []) as string[];
      const missingKeywords = (response.metadata?.missingKeywords ?? []) as string[];
      const matchPercentage = typeof response.score === "number" ? response.score : 0;

      setResult({
        matchPercentage,
        matchedKeywords,
        missingKeywords,
        suggestions: Array.isArray(response.suggestions) ? response.suggestions : [],
        summary: response.result,
      });
      reportAiOutcome(response.callId, "used");
    } catch {
      toast({
        variant: "error",
        title: t`Failed to analyze job match`,
        description: t`Please try again.`,
      });
    } finally {
      setLoading(false);
    }
  }, [gate, jobDescription, toast]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t`Job Description Matching`}
        </Label>
        <p className="text-[11px] text-muted-foreground">
          {t`Paste a job description to see how your resume matches and what keywords to add.`}
        </p>
      </div>

      <textarea
        value={jobDescription}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJobDescription(e.target.value)}
        placeholder={t`Paste the full job description here...`}
        className="min-h-[120px] w-full rounded-lg border border-border/50 bg-transparent px-3 py-2 text-xs outline-none transition-colors focus:border-primary/50"
      />

      <Button
        onClick={handleMatch}
        disabled={loading || !jobDescription.trim()}
        className="w-full"
        size="sm"
      >
        {loading ? (
          <>
            <CircleNotch size={14} className="mr-1.5 animate-spin" />
            {takingLonger ? t`Taking longer than usual…` : t`Analyzing...`}
          </>
        ) : (
          <>
            <MagnifyingGlass size={14} className="mr-1.5" weight="bold" />
            {t`Analyze Match`}
          </>
        )}
      </Button>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {result.matchPercentage > 0 && <MatchGauge percentage={result.matchPercentage} />}

            {result.summary && (
              <div className="rounded-lg border border-border/30 bg-secondary-accent/20 p-3">
                <div className="flex items-start gap-2">
                  <Lightning size={12} weight="fill" className="mt-0.5 shrink-0 text-primary" />
                  <p className="text-[11px] leading-relaxed text-foreground/60">
                    {result.summary}
                  </p>
                </div>
              </div>
            )}

            {result.matchedKeywords.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] font-medium text-foreground/60">
                  {t`Matched Keywords`}
                  <span className="ml-1.5 text-[10px] font-normal text-green-600">
                    ({result.matchedKeywords.length})
                  </span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {result.matchedKeywords.map((kw) => (
                    <KeywordBadge key={kw} keyword={kw} matched />
                  ))}
                </div>
              </div>
            )}

            {result.missingKeywords.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] font-medium text-foreground/60">
                  {t`Missing Keywords`}
                  <span className="ml-1.5 text-[10px] font-normal text-red-600">
                    ({result.missingKeywords.length})
                  </span>
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t`Click a keyword to add it to Skills, only if you actually have it.`}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {result.missingKeywords.map((kw) => (
                    <KeywordBadge key={kw} keyword={kw} matched={false} onAdd={addKeywordToSkills} />
                  ))}
                </div>
              </div>
            )}

            {result.suggestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] font-medium text-foreground/60">
                  {t`Suggestions`}
                </p>
                <div className="space-y-1.5">
                  {result.suggestions.map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * i }}
                      className="flex items-start gap-2 rounded-lg border border-primary/10 bg-primary/5 p-2.5"
                    >
                      <Lightning size={12} weight="fill" className="mt-0.5 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] leading-relaxed text-foreground/70">{s}</p>
                        <button
                          type="button"
                          className="mt-1 text-[10px] font-medium text-primary hover:underline"
                          onClick={() => askAssistantToApply(s)}
                        >
                          {t`Fix this in chat`}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                setResult(null);
                setJobDescription("");
              }}
            >
              <X size={12} className="mr-1" />
              {t`Clear & Start Over`}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
